import * as React from 'react';

import { parseDate, today, getLocalTimeZone } from '@internationalized/date';
import { enumCurrencyCode, enumUnit } from '@rematter/frontend-lib-api-client';
import {
  Button,
  ConsoleDialog,
  TextField,
  TextArea,
  Select,
  Switch,
  DatePicker,
  NumberField,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { Check } from 'lucide-react';

import {
  createOutboundTollingAgreementAtom,
  updateOutboundTollingAgreementAtom,
} from '../../_graphql/mutationOutboundTollingAgreement';
import { $remoteAllProcessingTypes } from '../../_state/processingTypes.state';
import { useTranslations } from '../../i18n/useTranslations';
import { OutboundTollingAgreementRecord } from '../../types/outboundTollingAgreement';

import { SelectAccount } from '~/shared/components/select-fields/SelectAccount';
import { SelectEnum } from '~/shared/components/select-fields/SelectEnum';
import { SelectFacility } from '~/shared/components/select-fields/SelectFacility';

type OutboundTollingDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  editingAgreement?: OutboundTollingAgreementRecord | null;
};

export const OutboundTollingDialog = ({
  isOpen,
  onClose,
  onSuccess,
  editingAgreement,
}: OutboundTollingDialogProps) => {
  const t = useTranslations();
  const toast = useToast();

  const [agreementNumber, setAgreementNumber] = React.useState('');
  const [processorAccountId, setProcessorAccountId] = React.useState<
    string | null
  >(null);
  const [processingTypeId, setProcessingTypeId] = React.useState('');
  const [effectiveDate, setEffectiveDate] = React.useState<string | null>(null);
  const [expirationDate, setExpirationDate] = React.useState<string | null>(
    null,
  );
  const [pricePerUnit, setPricePerUnit] = React.useState<number | null>(null);
  const [priceUnit, setPriceUnit] = React.useState<string | null>(null);
  const [expectedYieldPercentage, setExpectedYieldPercentage] = React.useState<
    number | null
  >(null);
  const [terms, setTerms] = React.useState('');
  const [isActive, setIsActive] = React.useState(true);
  const [facilityId, setFacilityId] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const { data: processingTypesData } = useAtomValue($remoteAllProcessingTypes);
  const processingTypes = React.useMemo(
    () => processingTypesData?.processingTypes ?? [],
    [processingTypesData?.processingTypes],
  );

  const createOutboundTollingAgreement = useSetAtom(
    createOutboundTollingAgreementAtom({}),
  );
  const updateOutboundTollingAgreement = useSetAtom(
    updateOutboundTollingAgreementAtom({}),
  );

  const isEditMode = !!editingAgreement;

  React.useEffect(() => {
    if (editingAgreement) {
      setAgreementNumber(editingAgreement.agreementNumber);
      setProcessorAccountId(editingAgreement.processorAccountId);
      setProcessingTypeId(editingAgreement.processingTypeId);
      setEffectiveDate(editingAgreement.effectiveDate);
      setExpirationDate(editingAgreement.expirationDate || null);
      setPricePerUnit(editingAgreement.pricePerUnitAmount || null);
      setPriceUnit(editingAgreement.priceUnit || null);
      setExpectedYieldPercentage(
        editingAgreement.expectedYieldPercentage || null,
      );
      setTerms(editingAgreement.terms || '');
      setIsActive(editingAgreement.isActive);
      setFacilityId(editingAgreement.facilityId);
    } else {
      setAgreementNumber('');
      setProcessorAccountId(null);
      setProcessingTypeId('');
      setEffectiveDate(null);
      setExpirationDate(null);
      setPricePerUnit(null);
      setPriceUnit(null);
      setExpectedYieldPercentage(null);
      setTerms('');
      setIsActive(true);
      setFacilityId(null);
    }
  }, [editingAgreement]);

  const handleClose = React.useCallback(() => {
    setAgreementNumber('');
    setProcessorAccountId(null);
    setProcessingTypeId('');
    setEffectiveDate(null);
    setExpirationDate(null);
    setPricePerUnit(null);
    setPriceUnit(null);
    setExpectedYieldPercentage(null);
    setTerms('');
    setIsActive(true);
    setFacilityId(null);
    onClose();
  }, [onClose]);

  const handleSubmit = React.useCallback(async () => {
    if (!agreementNumber.trim()) {
      toast.error(
        t.tollAgreementsView.dialog.validation.agreementNumberRequired,
      );
      return;
    }

    if (!isEditMode && !processorAccountId) {
      toast.error(t.tollAgreementsView.dialog.validation.processorRequired);
      return;
    }

    if (!isEditMode && !processingTypeId) {
      toast.error(
        t.tollAgreementsView.dialog.validation.processingTypeRequired,
      );
      return;
    }

    if (!isEditMode && !effectiveDate) {
      toast.error(t.tollAgreementsView.dialog.validation.effectiveDateRequired);
      return;
    }

    try {
      setIsSubmitting(true);

      if (isEditMode && editingAgreement) {
        await updateOutboundTollingAgreement({
          id: editingAgreement.id,
          agreementNumber: agreementNumber.trim(),
          expirationDate: expirationDate || undefined,
          pricePerUnit:
            pricePerUnit !== null
              ? { amount: pricePerUnit, currencyCode: enumCurrencyCode.USD }
              : undefined,
          priceUnit: priceUnit || undefined,
          expectedYieldPercentage: expectedYieldPercentage || undefined,
          terms: terms.trim() || undefined,
          isActive,
        });
        toast.success(t.tollAgreementsView.dialog.updateSuccess);
      } else if (processorAccountId && effectiveDate && facilityId) {
        await createOutboundTollingAgreement({
          agreementNumber: agreementNumber.trim(),
          processorAccountId,
          processingTypeId,
          effectiveDate,
          expirationDate: expirationDate || undefined,
          pricePerUnit:
            pricePerUnit !== null
              ? { amount: pricePerUnit, currencyCode: enumCurrencyCode.USD }
              : undefined,
          priceUnit: priceUnit || undefined,
          expectedYieldPercentage: expectedYieldPercentage || undefined,
          terms: terms.trim() || undefined,
          isActive,
          facilityId,
        });
        toast.success(t.tollAgreementsView.dialog.createSuccess);
      }

      onSuccess?.();
      handleClose();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'An error occurred';
      toast.error(
        isEditMode
          ? t.tollAgreementsView.dialog.updateError(errorMessage)
          : t.tollAgreementsView.dialog.createError(errorMessage),
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    agreementNumber,
    processorAccountId,
    processingTypeId,
    effectiveDate,
    expirationDate,
    pricePerUnit,
    priceUnit,
    expectedYieldPercentage,
    terms,
    isActive,
    facilityId,
    isEditMode,
    editingAgreement,
    createOutboundTollingAgreement,
    updateOutboundTollingAgreement,
    toast,
    t,
    onSuccess,
    handleClose,
  ]);

  const processingTypeOptions = React.useMemo(
    () => processingTypes.map((pt) => ({ key: pt.id, label: pt.name })),
    [processingTypes],
  );

  const canSubmit =
    Boolean(agreementNumber.trim()) &&
    (isEditMode || Boolean(processorAccountId)) &&
    (isEditMode || Boolean(processingTypeId)) &&
    (isEditMode || Boolean(effectiveDate)) &&
    (isEditMode || Boolean(facilityId)) &&
    !isSubmitting;

  const dialogTitle = isEditMode
    ? t.tollAgreementsView.dialog.editTitle
    : t.tollAgreementsView.dialog.createTitle;

  const submitButtonText = isEditMode
    ? isSubmitting
      ? t.tollAgreementsView.dialog.updating
      : t.tollAgreementsView.dialog.update
    : isSubmitting
      ? t.tollAgreementsView.dialog.creating
      : t.tollAgreementsView.dialog.create;

  return (
    <ConsoleDialog
      className="@OutboundTollingDialog"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      title={dialogTitle}
      size="wrap"
      content={
        <div className="space-y-4">
          <TextField
            label={t.tollAgreementsView.dialog.agreementNumberLabel}
            value={agreementNumber}
            onChange={setAgreementNumber}
            placeholder={t.tollAgreementsView.dialog.agreementNumberPlaceholder}
            isRequired
          />

          {!isEditMode && (
            <>
              <SelectAccount
                label={t.tollAgreementsView.dialog.processorLabel}
                value={processorAccountId}
                onChange={(value) => setProcessorAccountId(value ?? null)}
                placeholder={t.tollAgreementsView.dialog.processorPlaceholder}
                isRequired
              />

              <Select
                label={t.tollAgreementsView.dialog.processingTypeLabel}
                value={processingTypeId}
                onChange={(key) => setProcessingTypeId(String(key))}
                options={processingTypeOptions}
                placeholder={
                  t.tollAgreementsView.dialog.processingTypePlaceholder
                }
                isRequired
              />

              <SelectFacility
                label={t.tollAgreementsView.dialog.facilityLabel}
                value={facilityId}
                onChange={(value) => setFacilityId(value ?? null)}
                isRequired
              />
            </>
          )}

          <div className="grid grid-cols-2 gap-4">
            <DatePicker
              label={t.tollAgreementsView.dialog.effectiveDateLabel}
              value={
                effectiveDate ? parseDate(effectiveDate.split('T')[0]) : null
              }
              onChange={(date) =>
                setEffectiveDate(date ? date.toString() : null)
              }
              minValue={today(getLocalTimeZone())}
              isDisabled={isEditMode}
            />
            <DatePicker
              label={t.tollAgreementsView.dialog.expirationDateLabel}
              value={
                expirationDate ? parseDate(expirationDate.split('T')[0]) : null
              }
              onChange={(date) =>
                setExpirationDate(date ? date.toString() : null)
              }
              minValue={
                effectiveDate
                  ? parseDate(effectiveDate.split('T')[0])
                  : today(getLocalTimeZone())
              }
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <NumberField
              label={t.tollAgreementsView.dialog.pricePerUnitLabel}
              value={pricePerUnit ?? undefined}
              onChange={(val) => setPricePerUnit(val ?? null)}
              formatOptions={{ style: 'currency', currency: 'USD' }}
              minValue={0}
            />
            <SelectEnum
              label={t.tollAgreementsView.dialog.priceUnitLabel}
              value={priceUnit ?? undefined}
              onChange={(value) => setPriceUnit(value ?? null)}
              enumValue={enumUnit}
              placeholder={t.tollAgreementsView.dialog.priceUnitPlaceholder}
            />
          </div>

          <NumberField
            label={t.tollAgreementsView.dialog.expectedYieldLabel}
            value={expectedYieldPercentage ?? undefined}
            onChange={(val) => setExpectedYieldPercentage(val ?? null)}
            minValue={0}
            maxValue={100}
            formatOptions={{ style: 'percent', maximumFractionDigits: 1 }}
          />

          <TextArea
            label={t.tollAgreementsView.dialog.termsLabel}
            value={terms}
            onChange={setTerms}
            placeholder={t.tollAgreementsView.dialog.termsPlaceholder}
          />

          <div className="flex items-center gap-2">
            <Switch isSelected={isActive} onChange={setIsActive} />
            <span className="text-sm">
              {t.tollAgreementsView.dialog.isActiveLabel}
            </span>
          </div>
        </div>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            color="neutral"
            onPress={handleClose}
            isDisabled={isSubmitting}
          >
            {t.tollAgreementsView.dialog.cancel}
          </Button>
          <Button
            icon={<Check />}
            color="primary"
            onPress={handleSubmit}
            isDisabled={!canSubmit}
          >
            {submitButtonText}
          </Button>
        </div>
      }
    />
  );
};
